if (!customElements.get('cart-cross-sell-component')) {
  customElements.define(
    'cart-cross-sell-component',
    class CartCrossSellComponent extends HTMLElement {
      connectedCallback() {
        if (!this.querySelector('.cart-cross-sell__slider-track')) {
          requestAnimationFrame(() => this.init());
          return;
        }
        this.init();
      }

      disconnectedCallback() {
        this._initialized = false;
        if (this.animationFrameId) {
          cancelAnimationFrame(this.animationFrameId);
          this.animationFrameId = null;
        }
      }

      init() {
        if (this._initialized) return;
        this._initialized = true;

        this.track = this.querySelector('.cart-cross-sell__slider-track');
        this.prevBtn = this.querySelector('.cart-cross-sell__arrow--prev');
        this.nextBtn = this.querySelector('.cart-cross-sell__arrow--next');
        this.categorySelect = this.querySelector('.cart-cross-sell__category-select');
        this.initialTrackHtml = this.track ? this.track.innerHTML : '';
        this.selectedCategory = 'all';

        // Collections candidates complémentaires
        this.activeCollections = (this.dataset.activeCollections || '').split(',').filter(Boolean);
        this.cartProductIds = new Set((this.dataset.cartProductIds || '').split(',').map(Number));
        
        // Suivi des produits affichés et des quotas par collection (max 2 par collection)
        this.displayedProductIds = new Set();
        this.collectionCounts = {};
        this.querySelectorAll('.cart-cross-sell__card').forEach((card) => {
          const pid = Number(card.dataset.productId);
          const coll = card.dataset.collectionHandle;
          if (pid) this.displayedProductIds.add(pid);
          if (coll) {
            this.collectionCounts[coll] = (this.collectionCounts[coll] || 0) + 1;
          }
        });

        // File d'attente / Buffer mémoire pour injection instantanée à 0ms de latence
        this.productBuffer = [];
        this.collectionCache = new Map();
        this.currentCollIndex = 0;
        this.isBuffering = false;
        this.hasMore = true;

        this.setupNavigation();
        this.setupCategoryFilter();
        this.setupDragAndSwipe();
        this.setupCardEventListeners(this);
        this.setupInfiniteScroll();

        this.details = this.querySelector('.cart-cross-sell__accordion');
        if (this.details) {
          this.details.addEventListener('toggle', () => {
            if (this.details.open) {
              this.checkAndAppendFromBuffer();
            }
          });
        }

        // Préchargement immédiat en arrière-plan (sans bloquer le rendu)
        if (window.requestIdleCallback) {
          window.requestIdleCallback(() => this.refillBuffer(), { timeout: 300 });
        } else {
          setTimeout(() => this.refillBuffer(), 80);
        }
      }

      setupNavigation() {
        if (!this.track || !this.prevBtn || !this.nextBtn) return;

        this.prevBtn.addEventListener('click', () => {
          const card = this.track.querySelector('.cart-cross-sell__card');
          const cardStep = card ? (card.offsetWidth + 8) : 120;
          const targetX = Math.max(0, this.track.scrollLeft - cardStep * 2);
          this.smoothGlideTo(targetX, 600);
        });

        this.nextBtn.addEventListener('click', () => {
          this.checkAndAppendFromBuffer();
          const card = this.track.querySelector('.cart-cross-sell__card');
          const cardStep = card ? (card.offsetWidth + 8) : 120;
          const targetX = this.track.scrollLeft + cardStep * 2;
          this.smoothGlideTo(targetX, 600);
        });
      }

      setupCategoryFilter() {
        if (!this.categorySelect) return;

        this.categorySelect.addEventListener('change', async () => {
          const selectedHandle = this.categorySelect.value;
          this.selectedCategory = selectedHandle;

          if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
          }
          this.track.classList.remove('is-dragging');

          if (selectedHandle === 'all') {
            this.track.innerHTML = this.initialTrackHtml;
            this.track.scrollLeft = 0;
            this.productBuffer = [];
            this.displayedProductIds = new Set();
            this.collectionCounts = {};
            this.track.querySelectorAll('.cart-cross-sell__card').forEach((card) => {
              const pid = Number(card.dataset.productId);
              const coll = card.dataset.collectionHandle;
              if (pid) this.displayedProductIds.add(pid);
              if (coll) {
                this.collectionCounts[coll] = (this.collectionCounts[coll] || 0) + 1;
              }
            });
            this.currentCollIndex = 0;
            this.hasMore = true;
            this.setupCardEventListeners(this.track);
            this.refillBuffer();
          } else {
            this.track.innerHTML = '<div style="padding: 24px 8px; font-size: 11px; color: #888; display: flex; align-items: center; justify-content: center; width: 100%;">Chargement...</div>';
            this.track.scrollLeft = 0;
            this.productBuffer = [];
            this.displayedProductIds = new Set();
            this.hasMore = true;

            try {
              const prods = await this.fetchCollectionProducts(selectedHandle);
              const available = prods.filter((p) => !this.cartProductIds.has(p.id));

              this.track.innerHTML = '';
              if (available.length === 0) {
                this.track.innerHTML = '<div style="padding: 24px 8px; font-size: 11px; color: #888; display: flex; align-items: center; justify-content: center; width: 100%;">Aucun produit disponible</div>';
                this.hasMore = false;
                return;
              }

              const fragment = document.createDocumentFragment();
              const initialBatch = available.slice(0, 8);
              initialBatch.forEach((prod) => {
                this.displayedProductIds.add(prod.id);
                fragment.appendChild(this.buildCardElement(prod, selectedHandle));
              });
              this.track.appendChild(fragment);
              this.setupCardEventListeners(this.track);

              const remaining = available.slice(8);
              remaining.forEach((prod) => {
                this.displayedProductIds.add(prod.id);
                this.productBuffer.push({ product: prod, collectionHandle: selectedHandle });
              });
              this.hasMore = remaining.length > 0;
            } catch (err) {
              this.track.innerHTML = '<div style="padding: 24px 8px; font-size: 11px; color: #888; display: flex; align-items: center; justify-content: center; width: 100%;">Erreur de chargement</div>';
            }
          }
        });
      }

      smoothGlideTo(targetX, duration = 700) {
        if (!this.track) return;
        if (this.animationFrameId) {
          cancelAnimationFrame(this.animationFrameId);
          this.animationFrameId = null;
        }

        this.track.classList.add('is-dragging');
        const startPos = this.track.scrollLeft;
        const distance = targetX - startPos;
        if (Math.abs(distance) < 1) {
          this.track.classList.remove('is-dragging');
          return;
        }

        const startTime = performance.now();
        // Courbe d'atténuation progressive très douce (easeOutQuart)
        const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);

        const animate = (now) => {
          const elapsed = now - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const easedProgress = easeOutQuart(progress);

          this.track.scrollLeft = startPos + distance * easedProgress;
          this.checkAndAppendFromBuffer();

          if (progress < 1) {
            this.animationFrameId = requestAnimationFrame(animate);
          } else {
            this.animationFrameId = null;
            this.track.scrollLeft = targetX;
            this.track.classList.remove('is-dragging');
          }
        };

        this.animationFrameId = requestAnimationFrame(animate);
      }

      setupDragAndSwipe() {
        if (!this.track) return;

        // Bloquer le glisser-déposer d'image natif du navigateur
        this.track.addEventListener('dragstart', (e) => {
          e.preventDefault();
          return false;
        });

        let isMouseDown = false;
        let startX = 0;
        let scrollStart = 0;
        let hasDragged = false;
        let pointerId = null;
        let lastX = 0;
        let lastTime = 0;
        let velocityX = 0;

        const stopMomentum = () => {
          if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
          }
        };

        // Utiliser PointerEvents en filtrant strictement pour la souris
        this.track.addEventListener('pointerdown', (e) => {
          // Si c'est du tactile (mobile / tablette), NE RIEN TOUCHER (défilement matériel 100% natif)
          if (e.pointerType === 'touch') return;

          // Ne pas déclencher le drag sur les boutons d'action ou les sélecteurs
          if (e.target.closest('button, select, option')) return;
          if (e.button !== 0) return;

          stopMomentum();

          isMouseDown = true;
          hasDragged = false;
          startX = e.clientX;
          lastX = e.clientX;
          lastTime = performance.now();
          scrollStart = this.track.scrollLeft;
          velocityX = 0;
          pointerId = e.pointerId;
        });

        this.track.addEventListener('pointermove', (e) => {
          if (!isMouseDown || e.pointerType === 'touch') return;

          const now = performance.now();
          const dt = Math.max(1, now - lastTime);
          const dx = e.clientX - lastX;
          
          // Calcul de vitesse lissée pour l'impulsion
          velocityX = (dx / dt) * 0.7 + (velocityX * 0.3);
          lastX = e.clientX;
          lastTime = now;

          const walk = e.clientX - startX;
          if (Math.abs(walk) > 4) {
            if (!hasDragged) {
              hasDragged = true;
              this.track.classList.add('is-dragging');
              if (pointerId !== null) {
                try {
                  this.track.setPointerCapture(pointerId);
                } catch (err) {}
              }
            }
          }
          if (hasDragged) {
            e.preventDefault();
            this.track.scrollLeft = scrollStart - walk;
            this.checkAndAppendFromBuffer();
          }
        });

        const endDrag = (e) => {
          if (!isMouseDown || e.pointerType === 'touch') return;
          isMouseDown = false;

          if (pointerId !== null) {
            try {
              this.track.releasePointerCapture(pointerId);
            } catch (err) {}
            pointerId = null;
          }

          if (hasDragged) {
            const card = this.track.querySelector('.cart-cross-sell__card');
            const cardStep = card ? (card.offsetWidth + 8) : 120;

            // Prédire le point d'atterrissage selon l'inertie du geste
            const momentumOffset = -velocityX * 350;
            const projectedX = this.track.scrollLeft + momentumOffset;

            // Aligner en douceur sur la carte la plus proche
            const targetIndex = Math.max(0, Math.round(projectedX / cardStep));
            const finalTargetX = targetIndex * cardStep;

            // Durée d'animation calme et fluide (entre 650ms et 850ms)
            const travel = Math.abs(finalTargetX - this.track.scrollLeft);
            const duration = Math.min(850, Math.max(650, 500 + travel * 0.35));

            this.smoothGlideTo(finalTargetX, duration);

            // Réinitialiser hasDragged après un court délai pour bloquer le clic accidentel sur lien
            setTimeout(() => {
              hasDragged = false;
              this.track.classList.remove('is-dragging');
            }, 100);
          } else {
            this.track.classList.remove('is-dragging');
            hasDragged = false;
          }
        };

        this.track.addEventListener('pointerup', endDrag);
        this.track.addEventListener('pointercancel', endDrag);

        // Empêcher l'ouverture d'un lien UNIQUEMENT si la souris a réellement glissé
        this.track.addEventListener('click', (e) => {
          if (hasDragged) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }

          // Si c'est un clic simple sur une image ou un titre de produit, laisser naviguer
          const link = e.target.closest('a.cart-cross-sell__card-image-link, a.cart-cross-sell__card-title');
          if (link && link.href) {
            window.location.href = link.href;
          }
        }, false);
      }

      setupInfiniteScroll() {
        if (!this.track) return;

        let ticking = false;
        this.track.addEventListener('scroll', () => {
          if (!ticking) {
            window.requestAnimationFrame(() => {
              this.checkAndAppendFromBuffer();
              ticking = false;
            });
            ticking = true;
          }
        }, { passive: true });
      }

      checkAndAppendFromBuffer() {
        if (!this.track || !this.hasMore) return;

        const remainingScroll = this.track.scrollWidth - this.track.scrollLeft - this.track.clientWidth;
        const threshold = Math.max(this.track.clientWidth * 1.5, 450);

        if (remainingScroll <= threshold) {
          this.flushBufferToDOM(6);
        }
      }

      flushBufferToDOM(count = 6) {
        if (this.productBuffer.length === 0) {
          this.refillBuffer();
          return;
        }

        const toAppend = this.productBuffer.splice(0, count);
        if (toAppend.length > 0) {
          const fragment = document.createDocumentFragment();
          toAppend.forEach(({ product, collectionHandle }) => {
            const cardEl = this.buildCardElement(product, collectionHandle);
            fragment.appendChild(cardEl);
          });
          this.track.appendChild(fragment);
          this.setupCardEventListeners(this.track);
        }

        // Réapprovisionner le buffer en arrière-plan
        this.refillBuffer();
      }

      async fetchCollectionProducts(collHandle) {
        if (this.collectionCache.has(collHandle)) {
          return this.collectionCache.get(collHandle);
        }
        try {
          const res = await fetch(`/collections/${collHandle}/products.json?limit=30`);
          if (!res.ok) return [];
          const data = await res.json();
          const prods = data.products || [];
          this.collectionCache.set(collHandle, prods);
          return prods;
        } catch (e) {
          return [];
        }
      }

      async refillBuffer() {
        if (this.isBuffering || !this.hasMore || this.productBuffer.length >= 12) return;
        if (this.selectedCategory && this.selectedCategory !== 'all') return;
        this.isBuffering = true;

        const targetBufferCount = 12;
        let attempts = 0;
        const maxAttempts = this.activeCollections.length * 3 + 6;

        while (this.productBuffer.length < targetBufferCount && attempts < maxAttempts) {
          attempts++;
          const collHandle = this.activeCollections[this.currentCollIndex % this.activeCollections.length];
          this.currentCollIndex++;

          if (!collHandle) continue;

          const prods = await this.fetchCollectionProducts(collHandle);
          // Filtrer les produits disponibles et pas encore affichés ni dans le panier
          const available = prods.filter((p) => !this.cartProductIds.has(p.id) && !this.displayedProductIds.has(p.id));

          // Prendre strictement les 2 prochains best-sellers de cette catégorie (ordre Shopify par défaut)
          let addedForThisColl = 0;
          for (const prod of available) {
            if (this.productBuffer.length >= targetBufferCount || addedForThisColl >= 2) break;

            this.displayedProductIds.add(prod.id);
            this.collectionCounts[collHandle] = (this.collectionCounts[collHandle] || 0) + 1;
            this.productBuffer.push({ product: prod, collectionHandle: collHandle });
            addedForThisColl++;
          }
        }

        if (this.productBuffer.length < targetBufferCount && attempts >= maxAttempts) {
          try {
            let fallbackProds = this.collectionCache.get('all_fallback');
            if (!fallbackProds) {
              const res = await fetch('/products.json?limit=40');
              const data = await res.json();
              fallbackProds = data.products || [];
              this.collectionCache.set('all_fallback', fallbackProds);
            }
            const availableFallback = fallbackProds.filter((p) => !this.cartProductIds.has(p.id) && !this.displayedProductIds.has(p.id));
            for (const p of availableFallback) {
              if (this.productBuffer.length >= targetBufferCount) break;
              this.displayedProductIds.add(p.id);
              this.productBuffer.push({ product: p, collectionHandle: 'general' });
            }
          } catch (e) {}
        }

        if (this.productBuffer.length === 0 && attempts >= maxAttempts) {
          this.hasMore = false;
        }

        this.isBuffering = false;

        // Si le slider a besoin de cartes immédiatement
        const remainingScroll = this.track.scrollWidth - this.track.scrollLeft - this.track.clientWidth;
        if (remainingScroll <= Math.max(this.track.clientWidth * 1.5, 450) && this.productBuffer.length > 0) {
          this.flushBufferToDOM(6);
        }
      }

      buildCardElement(product, collectionHandle) {
        const firstVariant = (product.variants || []).find((v) => v.available) || (product.variants || [])[0] || {};
        
        const parsePrice = (val) => {
          if (val === null || val === undefined || val === '') return 0;
          if (typeof val === 'number') {
            return val > 1000 ? val / 100 : val;
          }
          const clean = String(val).replace(/[^\d.,]/g, '').replace(',', '.');
          const num = parseFloat(clean);
          if (isNaN(num)) return 0;
          return num > 1000 ? num / 100 : num;
        };

        const formatPrice = (num) => {
          return num.toFixed(2).replace('.', ',') + ' €';
        };

        const p = parsePrice(firstVariant.price || product.price);
        const cp = parsePrice(firstVariant.compare_at_price || product.compare_at_price || product.compare_at_price_max);
        
        const hasDiscount = cp > p && cp > 0 && p > 0;
        const discountPercentage = hasDiscount ? Math.round(((cp - p) / cp) * 100) : 0;

        const imageSrc = (product.images && product.images[0] && product.images[0].src) || product.featured_image || '';
        const url = product.handle ? `/products/${product.handle}` : '#';

        const sizeSystem = this.dataset.sizeSystem || 'EU';

        const localizeVariantTitle = (title, sizeSys = 'EU') => {
          if (!title || typeof title !== 'string') return title;
          const tLower = title.toLowerCase();
          if (tLower.includes('eu') && tLower.includes('us')) {
            const rawTokens = title.split('/').map((t) => t.trim());
            let nonSizeTokens = [];
            let sizeEu = '', sizeUs = '', sizeUk = '';
            rawTokens.forEach((token) => {
              const up = token.toUpperCase();
              if (up.includes('EU') || up.includes('FR')) {
                sizeEu = token;
              } else if (up.includes('US')) {
                sizeUs = token;
              } else if (up.includes('UK')) {
                sizeUk = token;
              } else {
                nonSizeTokens.push(token);
              }
            });

            let chosenSize = sizeEu;
            if (sizeSys === 'UK' && sizeUk) chosenSize = sizeUk;
            else if (sizeSys === 'US' && sizeUs) chosenSize = sizeUs;
            else if (!chosenSize) chosenSize = sizeUs || sizeUk;

            if (chosenSize) {
              return nonSizeTokens.length ? `${nonSizeTokens.join(' / ')} / ${chosenSize}` : chosenSize;
            }
          }
          return title;
        };

        let variantOptionsHtml = '';
        if (product.variants && product.variants.length > 1) {
          const availableVariants = product.variants.filter((v) => v.available !== false);
          const opts = (availableVariants.length ? availableVariants : product.variants)
            .map((v) => {
              let vImg = '';
              if (v.featured_image && v.featured_image.src) {
                vImg = v.featured_image.src;
              } else if (v.image_id && product.images) {
                const found = product.images.find((img) => img.id === v.image_id);
                if (found) vImg = found.src;
              }
              if (!vImg && imageSrc) vImg = imageSrc;

              const vp = parsePrice(v.price);
              const vcp = parsePrice(v.compare_at_price);
              const vDisc = (vcp > vp && vcp > 0 && vp > 0) ? Math.round(((vcp - vp) / vcp) * 100) : 0;
              const vCompFormatted = (vcp > vp && vcp > 0 && vp > 0) ? formatPrice(vcp) : '';
              const displayTitle = localizeVariantTitle(v.title, sizeSystem);

              return `<option value="${v.id}" ${v.id === firstVariant.id ? 'selected' : ''} data-image="${vImg}" data-price="${formatPrice(vp)}" data-compare-price="${vCompFormatted}" data-discount="${vDisc}">${displayTitle}</option>`;
            })
            .join('');
          variantOptionsHtml = `<select class="cart-cross-sell__card-variant-select" aria-label="Variante">${opts}</select>`;
        } else {
          variantOptionsHtml = `<input type="hidden" class="cart-cross-sell__card-variant-id" value="${firstVariant.id || ''}">`;
        }

        const card = document.createElement('div');
        card.className = 'cart-cross-sell__card';
        card.dataset.productId = product.id;
        card.dataset.collectionHandle = collectionHandle;

        const badgesHtml = (hasDiscount && discountPercentage > 0)
          ? `<span class="cart-cross-sell__perc-badge">-${discountPercentage}%</span>`
          : '';

        const isInCart = this.cartProductIds && this.cartProductIds.has(product.id);

        card.innerHTML = `
          <div class="cart-cross-sell__card-media">
            <a href="${url}" class="cart-cross-sell__card-image-link" aria-label="${product.title || ''}">
              ${imageSrc ? `<img src="${imageSrc}" alt="${product.title || ''}" class="cart-cross-sell__card-img" loading="lazy" width="110" height="140" draggable="false">` : `<div class="cart-cross-sell__card-img-placeholder"></div>`}
            </a>
            ${badgesHtml}
            <button
              type="button"
              class="cart-cross-sell__card-quick-add${isInCart ? ' is-added' : ''}"
              data-variant-id="${firstVariant.id || ''}"
              aria-label="${isInCart ? 'Ajouté au panier' : `Ajouter ${product.title || ''} au panier`}"
              title="${isInCart ? 'Ajouté au panier' : 'Ajouter au panier'}"
            >
              <span class="cart-cross-sell__card-quick-add-icon">
                <svg viewBox="0 0 20 20" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                  <line x1="10" y1="4" x2="10" y2="16"></line>
                  <line x1="4" y1="10" x2="16" y2="10"></line>
                </svg>
              </span>
              <span class="cart-cross-sell__card-quick-add-check">
                <svg viewBox="0 0 20 20" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="4 10 8 14 16 6"></polyline>
                </svg>
              </span>
              <span class="cart-cross-sell__card-quick-add-spinner">
                <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.5" class="cart-cross-sell__spin">
                  <circle cx="12" cy="12" r="10" stroke-opacity="0.25"></circle>
                  <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"></path>
                </svg>
              </span>
            </button>
          </div>
          <div class="cart-cross-sell__card-content">
            <a href="${url}" class="cart-cross-sell__card-title" title="${product.title || ''}">
              ${product.title || ''}
            </a>
            <div class="cart-cross-sell__card-prices">
              <span class="cart-cross-sell__card-price">${formatPrice(p)}</span>
              ${hasDiscount ? `<span class="cart-cross-sell__card-compare-price">${formatPrice(cp)}</span>` : ''}
            </div>
            ${variantOptionsHtml}
          </div>
        `;

        return card;
      }

      setupCardEventListeners(root = this) {
        root.querySelectorAll('.cart-cross-sell__card').forEach((card) => {
          if (card.getAttribute('data-initialized') === 'true') return;
          card.setAttribute('data-initialized', 'true');
          const select = card.querySelector('.cart-cross-sell__card-variant-select');
          const btn = card.querySelector('.cart-cross-sell__card-quick-add');
          const hiddenInput = card.querySelector('.cart-cross-sell__card-variant-id');
          const img = card.querySelector('.cart-cross-sell__card-img');

          if (select && btn) {
            select.addEventListener('change', () => {
              const selectedOption = select.options[select.selectedIndex];
              if (!selectedOption) return;

              btn.setAttribute('data-variant-id', select.value);

              // 1. Image
              if (img) {
                const variantImg = selectedOption.getAttribute('data-image');
                if (variantImg) {
                  img.src = variantImg;
                }
              }

              // 2. Prix et Prix comparé
              const priceEl = card.querySelector('.cart-cross-sell__card-price');
              const pricesContainer = card.querySelector('.cart-cross-sell__card-prices');
              const varPrice = selectedOption.getAttribute('data-price');
              const varCompare = selectedOption.getAttribute('data-compare-price');
              const varDiscount = parseInt(selectedOption.getAttribute('data-discount') || '0', 10);

              if (priceEl && varPrice) {
                priceEl.textContent = varPrice;
              }

              let compareEl = card.querySelector('.cart-cross-sell__card-compare-price');
              if (varCompare) {
                if (!compareEl && pricesContainer) {
                  compareEl = document.createElement('span');
                  compareEl.className = 'cart-cross-sell__card-compare-price';
                  pricesContainer.appendChild(compareEl);
                }
                if (compareEl) compareEl.textContent = varCompare;
              } else if (compareEl) {
                compareEl.remove();
              }

              // 3. Badge % Réduction sur l'image
              const mediaContainer = card.querySelector('.cart-cross-sell__card-media');
              let percBadge = card.querySelector('.cart-cross-sell__perc-badge');

              if (varDiscount > 0) {
                if (!percBadge && mediaContainer) {
                  percBadge = document.createElement('span');
                  percBadge.className = 'cart-cross-sell__perc-badge';
                  mediaContainer.appendChild(percBadge);
                }
                if (percBadge) {
                  percBadge.textContent = `-${varDiscount}%`;
                }
              } else {
                if (percBadge) percBadge.remove();
              }
            });
          }

          if (btn && !btn._bound) {
            btn._bound = true;
            btn.addEventListener('click', async (e) => {
              e.preventDefault();
              e.stopPropagation();

              if (btn.classList.contains('is-loading')) return;

              const variantId = select ? select.value : (hiddenInput ? hiddenInput.value : btn.getAttribute('data-variant-id'));
              if (!variantId) return;

              btn.classList.remove('is-added');
              btn.classList.add('is-loading');

              try {
                // Ajout par l'API unique du panier (affichage optimiste, file serialisee, reconciliation serveur).
                const vc = /** @type {any} */ (window).ValeraneCart;
                if (!vc?.add) throw new Error('ValeraneCart indisponible');
                const opt = select ? select.options[select.selectedIndex] : null;
                const toCents = (/** @type {string | null | undefined} */ s) => {
                  const d = String(s || '').replace(/[^0-9]/g, '');
                  return d ? parseInt(d, 10) : 0;
                };
                const cardImg = card.querySelector('.cart-cross-sell__card-img');
                const comparePrice = opt?.getAttribute('data-compare-price') || card.querySelector('.cart-cross-sell__card-compare-price')?.textContent;
                const result = await vc.add({
                  id: variantId,
                  quantity: 1,
                  context: 'cross-sell',
                  source: card,
                  preview: {
                    title: card.querySelector('.cart-cross-sell__card-title')?.textContent?.trim() || '',
                    variantTitle: opt && select.options.length > 1 ? opt.textContent.trim() : '',
                    image: opt?.getAttribute('data-image') || cardImg?.getAttribute('src') || '',
                    price: toCents(opt?.getAttribute('data-price') || card.querySelector('.cart-cross-sell__card-price')?.textContent),
                    compareAtPrice: toCents(comparePrice) || undefined,
                    url: card.querySelector('a.cart-cross-sell__card-title')?.getAttribute('href') || undefined,
                  },
                });
                if (!result?.ok) throw new Error(result?.message || 'Add to cart failed');

                // Succes : marquer le bouton comme valide
                btn.classList.remove('is-loading');
                btn.classList.add('is-added');
                btn.setAttribute('title', 'Ajouté au panier');
                btn.setAttribute('aria-label', 'Ajouté au panier');

                const prodId = Number(card.dataset.productId);
                if (prodId && this.cartProductIds) {
                  this.cartProductIds.add(prodId);
                }
              } catch (err) {
                console.error('Erreur ajout cross-sell:', err);
                btn.classList.remove('is-loading');
              }
            });
          }
        });
      }
    }
  );
}
